import { ButtonSecondary, Colors, Container, Spinner } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { OBJECTIVES_PAGE_LAYOUT } from '@/components/objectives/objectiveStyles';

/** The centred spinner an Objectives page shows in place of a section still being read. */
export function ObjectiveSectionLoader({ dataTestId }: { dataTestId: string }) {
    return (
        <div className={'flex justify-center py-6'}>
            <Spinner
                size={'md'}
                color={Colors.PRIMARY}
                dataTestId={dataTestId}
            />
        </div>
    );
}

interface ObjectiveLoadErrorMessageProps {
    /** A section that can name what it failed to read says so; the rest keep the shared sentence. */
    message?: string;
    dataTestId?: string;
}

/** What an Objectives page says in place of a section it could not read: always the same alert,
 * the same colour and the same spacing, whatever sentence the section puts in it. */
export function ObjectiveLoadErrorMessage({
    message,
    dataTestId,
}: ObjectiveLoadErrorMessageProps = {}) {
    const { t } = useTranslation();

    return (
        <p
            role={'alert'}
            className={'text-error py-6'}
            data-testid={dataTestId}
        >
            {message ?? t('objectives.errors.loading')}
        </p>
    );
}

/** The same spinner, standing in for a whole page rather than one of its sections. */
export function ObjectiveLoadingState({ dataTestId }: { dataTestId: string }) {
    return (
        <Container className={OBJECTIVES_PAGE_LAYOUT.shell}>
            <ObjectiveSectionLoader dataTestId={dataTestId} />
        </Container>
    );
}

interface ObjectiveMessageStateProps {
    /** Text shown instead of the page: a load failure, or "objective not found". */
    message: string;
    /** A failure is red; a missing objective is neutral grey. */
    isError: boolean;
    onBack: () => void;
    backLabel: string;
    backDataTestId: string;
}

/** What the form and detail pages render when the objective they need cannot be shown. */
export function ObjectiveMessageState({
    message,
    isError,
    onBack,
    backLabel,
    backDataTestId,
}: ObjectiveMessageStateProps) {
    return (
        <Container className={OBJECTIVES_PAGE_LAYOUT.shell}>
            <div className={'mx-3 mt-6 flex flex-col items-start gap-4'}>
                {/* No radius override: the button keeps the library's own `shape="rounded"`. */}
                <ButtonSecondary
                    onClick={onBack}
                    size={'sm'}
                    dataTestId={backDataTestId}
                >
                    {backLabel}
                </ButtonSecondary>

                <p
                    role={'alert'}
                    className={isError ? 'text-error' : 'text-gray-500'}
                >
                    {message}
                </p>
            </div>
        </Container>
    );
}
